import React from 'react';
import classnames from 'classnames';

import { AutoCompleteWrapper, Input, InputWrapper, LabelWrapper } from './../TextInputStyles';
import useScreenQuery from '../../../hooks/useScreenQuery';
import PlayupThemeProvider from '../../../themes/PlayupThemeProvider';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';

interface AutocompleteProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> {
	kind?: string;
	label?: string;
	error?: string;
	initialValue?: string;
	queryElements: (query: string) => Promise<Array<AutoCompleteElement<unknown>>>;
	onSelectElement: (selectedElement: AutoCompleteElement<unknown> | undefined) => void;
}

export interface AutoCompleteElement<T> {
	label: string;
	id: string;
	element: T;
}

const TextInputAutocomplete = React.forwardRef<HTMLInputElement, AutocompleteProps>(
	(
		{
			kind = 'blackslate',
			children,
			className,
			initialValue = '',
			error,
			id,
			label,
			onKeyDown,
			onSelectElement,
			queryElements,
			...rest
		},
		ref
	) => {
		const { isSmall } = useScreenQuery();
		const [displayAutocompleteElements, setDisplayAutocompleteElements] = React.useState(false);
		const inputId = React.useMemo(() => id || `text-input-${Math.random()}`, [id]);
		const [inputValue, setInputValue] = React.useState(initialValue);
		const inputRef = React.useRef(inputValue);
		const [autocompleteElements, setAutocompleteElements] = React.useState<Array<AutoCompleteElement<unknown>>>();
		const [activeAutocompleteElement, setActiveAutocompleteElement] = React.useState<AutoCompleteElement<unknown>>();
		const [selectedAutocompleteElement, setSelectedAutocompleteElement] =
			React.useState<AutoCompleteElement<unknown>>();
		const [previousSelectedAutocompleteElement, setPreviousSelectedAutocompleteElement] =
			React.useState<AutoCompleteElement<unknown>>();

		// When the input changes
		React.useEffect(() => {
			inputRef.current = inputValue;

			if (!inputValue) {
				setAutocompleteElements(undefined);
				return;
			}

			queryElements(inputValue).then((results) => {
				// to make sure this is not the result of an old promise
				if (inputRef.current !== inputValue) return;
				setAutocompleteElements(results);
			});
		}, [inputValue, queryElements]);

		// When an element is selected
		React.useEffect(() => {
			if (!selectedAutocompleteElement) return;
			if (selectedAutocompleteElement.id === previousSelectedAutocompleteElement?.id) return;

			setAutocompleteElements(undefined); // delete old results
			setActiveAutocompleteElement(undefined); // delete old active status
			setPreviousSelectedAutocompleteElement(selectedAutocompleteElement); // set current as previous as it will be consumed soon
			if (selectedAutocompleteElement?.label) setInputValue(selectedAutocompleteElement?.label);

			onSelectElement(selectedAutocompleteElement);
		}, [selectedAutocompleteElement, previousSelectedAutocompleteElement, onSelectElement]);

		const onFocusHandler = React.useCallback(() => {
			setDisplayAutocompleteElements(true);
		}, []);

		const keydownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key) && autocompleteElements?.length) {
				e.preventDefault();
				const currentActiveIndex = activeAutocompleteElement
					? autocompleteElements?.indexOf(activeAutocompleteElement)
					: null;

				if (e.key === 'ArrowDown') {
					const nextIndex = currentActiveIndex !== null ? currentActiveIndex + 1 : 0;

					setActiveAutocompleteElement(autocompleteElements[nextIndex]);
				}

				if (e.key === 'ArrowUp') {
					setActiveAutocompleteElement(
						autocompleteElements[currentActiveIndex !== null ? currentActiveIndex - 1 : autocompleteElements.length - 1]
					);
				}

				if (e.key === 'Enter') {
					if (activeAutocompleteElement) {
						setSelectedAutocompleteElement(activeAutocompleteElement);
					}
				}

				if (e.key === 'Escape') {
					setAutocompleteElements(undefined);
				}
			}
			if (onKeyDown) onKeyDown(e);
		};

		const clickHandler = (autoCompleteElement: AutoCompleteElement<unknown>) => () => {
			setSelectedAutocompleteElement(autoCompleteElement);
		};

		const showAutoComplete = !!autocompleteElements && !selectedAutocompleteElement && displayAutocompleteElements;

		return (
			<PlayupThemeProvider>
				<div className={className}>
					<LabelWrapper kind={kind} $isSmallScreen={isSmall}>
						{label && <label htmlFor={inputId}>{label}</label>}
						{children}
					</LabelWrapper>
					<InputWrapper>
						<Input
							$isSmallScreen={isSmall}
							ref={ref}
							id={inputId}
							kind={kind}
							className={classnames(error && 'error')}
							onKeyDown={keydownHandler}
							value={inputValue}
							onChange={(e) => {
								setSelectedAutocompleteElement(undefined);
								setInputValue(e.target.value);
							}}
							onFocus={onFocusHandler}
							{...rest}
						/>
						{showAutoComplete && (
							<AutoCompleteWrapper kind={kind}>
								{autocompleteElements && autocompleteElements.length > 0 ? (
									autocompleteElements.map((autoCompleteElement) => (
										<li
											key={autoCompleteElement.id}
											className={classnames(autoCompleteElement.id === activeAutocompleteElement?.id && 'active')}
											onClick={clickHandler(autoCompleteElement)}
										>
											{autoCompleteElement.label}
										</li>
									))
								) : (
									<li className="neutral">No results</li>
								)}
							</AutoCompleteWrapper>
						)}
						{!showAutoComplete}
					</InputWrapper>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</div>
			</PlayupThemeProvider>
		);
	}
);

export default TextInputAutocomplete;
